'use client';

import { useEffect, useMemo, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { applyTranslations, type Product } from '@/lib/products';
import { useTranslation } from '@/context/LanguageContext';
import ProductCard from '@/components/ProductCard';
import WaveDivider from '@/components/WaveDivider';
import DoubleWaveDivider from '@/components/DoubleWaveDivider';
import styles from './page.module.scss';

function featuredMainImageSrc(product: Product): string {
  const base = product.image?.trim() || '';
  const card = product.cardImage?.trim() || '';
  return card || base || '/media/site/about-scan.webp';
}

export default function HomeContent() {
  const { t } = useTranslation();
  const [products, setProducts] = useState<Product[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(true);

  useEffect(() => {
    let cancelled = false;
    fetch('/api/products', { cache: 'no-store' })
      .then((r) => r.json())
      .then((data: unknown) => {
        if (cancelled) return;
        setProducts(Array.isArray(data) ? (data as Product[]) : []);
      })
      .catch(() => {
        if (cancelled) return;
        setProducts([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingProducts(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const translatedProducts = useMemo(
    () => products.map((p) => applyTranslations(p, t)),
    [products, t]
  );
  const featured = translatedProducts[0];
  const heroTitleLines = t.home.hero.title.split('\n');

  return (
    <>
      <section className={styles.hero}>
        <div className={styles.heroVideoWrap}>
          <video
            className={styles.heroVideo}
            autoPlay
            muted
            loop
            playsInline
            poster="/media/site/about-scan.webp"
          >
            <source src="/media/site/hero-video.mp4" type="video/mp4" />
          </video>
          <div className={styles.heroOverlay} />
        </div>
        <div className={styles.heroContent}>
          <h1 className={styles.heroTitle}>
            {heroTitleLines.map((line, index) => (
              <span key={index}>
                {line}
                {index < heroTitleLines.length - 1 && <br />}
              </span>
            ))}
          </h1>
          <p className={styles.heroSub}>
            {t.home.hero.subtitle}
          </p>
          <Link href="/shop" className={styles.heroCta}>
            {t.home.hero.cta}
          </Link>
        </div>
        <div className={styles.scrollHint} aria-hidden="true">
          <span />
        </div>
      </section>

      <section className={styles.bannerStrip}>
        <div className={styles.bannerTrack}>
          {[...t.home.banner.items, ...t.home.banner.items].map((item, i) => (
            <span key={i}>{i > 0 && <span>&bull;</span>}{item}</span>
          ))}
        </div>
      </section>

      <DoubleWaveDivider flip />

      <section className={styles.parallaxBreak} aria-label={t.home.mosaic.alt1} />

      <DoubleWaveDivider />

      {!loadingProducts && featured && (
        <section className={`${styles.featured} section section--magenta`}>
          <div className="container">
            <div className={styles.featuredGrid}>
              <div className={styles.featuredImage}>
                <Image
                  src={featuredMainImageSrc(featured)}
                  alt={featured.name}
                  width={1200}
                  height={1200}
                  className={styles.featuredVideo}
                />
              </div>
              <div className={styles.featuredText}>
                <p className={styles.featuredEyebrow}>{t.home.featured.eyebrow}</p>
                <h2 className={styles.featuredName}>{featured.name}</h2>
                <p className={styles.featuredFlavor}>{featured.flavor}</p>
                <p className={styles.featuredDesc}>{featured.description}</p>
                <Link href={`/shop/${featured.slug}`} className={styles.featuredCta}>
                  {t.home.featured.cta}
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}

      <WaveDivider from="magenta" />

      <section className={styles.products}>
        <div className="container">
          <p className={styles.productsEyebrow}>{t.home.products.eyebrow}</p>
          <h2 className={styles.productsTitle}>{t.home.products.title}</h2>
          {loadingProducts ? (
            <p className={styles.productsLoading}>Indlæser produkter…</p>
          ) : (
            <div className={styles.grid}>
              {translatedProducts.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          )}
        </div>
      </section>

      <section className={styles.quote}>
        <div className="container">
          <blockquote className={styles.quoteText}>
            &ldquo;{t.home.quote.text}&rdquo;
          </blockquote>
          <p className={styles.quoteAttr}>{t.home.quote.attribution}</p>
        </div>
      </section>

      <WaveDivider from="yellow" />

      <section className={`${styles.howItWorks} section section--magenta`}>
        <div className="container">
          <p className={styles.hiwEyebrow}>{t.home.howItWorks.eyebrow}</p>
          <h2 className={styles.hiwTitle}>{t.home.howItWorks.title}</h2>
          <div className={styles.hiwGrid}>
            <div className={styles.hiwStep}>
              <span className={styles.hiwNumber}>01</span>
              <h3>{t.home.howItWorks.step1Title}</h3>
              <p>{t.home.howItWorks.step1Body}</p>
            </div>
            <div className={styles.hiwStep}>
              <span className={styles.hiwNumber}>02</span>
              <h3>{t.home.howItWorks.step2Title}</h3>
              <p>{t.home.howItWorks.step2Body}</p>
            </div>
            <div className={styles.hiwStep}>
              <span className={styles.hiwNumber}>03</span>
              <h3>{t.home.howItWorks.step3Title}</h3>
              <p>{t.home.howItWorks.step3Body}</p>
            </div>
          </div>
          <div className={styles.hiwCta}>
            <Link href="/abonnement" className={styles.hiwBtn}>
              {t.home.howItWorks.cta}
            </Link>
          </div>
        </div>
      </section>

      <WaveDivider from="magenta" />

      <section className={styles.finalCta}>
        <div className="container">
          <h2 className={styles.finalCtaTitle}>{t.home.finalCta.title}</h2>
          <p className={styles.finalCtaSub}>
            {t.home.finalCta.body}
          </p>
          <div className={styles.finalCtaButtons}>
            <Link href="/shop" className={styles.ctaBtnPrimary}>
              {t.home.finalCta.primaryCta}
            </Link>
            <Link href="/om" className={styles.ctaBtnOutline}>
              {t.home.finalCta.secondaryCta}
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
